/**
 * The Phira batch-export picker (W2-09 publish-import; DESIGN.md §5.12 step 2, P§6.1): a package holding
 * `export.json` and nested chart packages. The user chooses which charts to import; each becomes its own import in
 * the queue (one project each). Rendered by the import banner host while the queue waits for the choice.
 */

import { useState } from 'react';
import { Button } from '../../ui/Button';
import { Checkbox } from '../../ui/Checkbox';
import { Dialog } from '../../ui/Dialog';
import type { Text } from './hooks';
import { m } from './import.messages';
import type { BatchPrompt } from './importQueue';
import s from './Import.module.css';

const SIZE_UNITS = [m.sizeKB, m.sizeMB, m.sizeGB] as const;

/** "2.5 MB" style size (decimal units, as file managers show them; locale digits, translated unit). */
export function formatSize(bytes: number, text: Pick<Text, 'locale' | 't'>): string {
  const number = (v: number, digits: number): string =>
    new Intl.NumberFormat(text.locale, { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(v);
  const b = Number.isFinite(bytes) && bytes > 0 ? bytes : 0;
  if (b < 1000) return text.t(m.sizeB, { size: number(Math.round(b), 0) });
  let v = b / 1000;
  let i = 0;
  while (v >= 1000 && i < SIZE_UNITS.length - 1) {
    v /= 1000;
    i++;
  }
  return text.t(SIZE_UNITS[i] ?? m.sizeGB, { size: number(v, 1) });
}

export interface BatchPickerProps {
  readonly text: Text;
  readonly prompt: BatchPrompt;
  /** Indices of the chosen charts (in list order), or null when cancelled. */
  readonly onDone: (picked: readonly number[] | null) => void;
}

export function BatchPicker(props: BatchPickerProps) {
  const { text, prompt } = props;
  const charts = prompt.charts;
  const [chosen, setChosen] = useState<ReadonlySet<number>>(() => new Set(charts.length > 0 ? [0] : []));
  const all = chosen.size === charts.length;
  const toggle = (i: number, on: boolean): void => {
    const next = new Set(chosen);
    if (on) next.add(i);
    else next.delete(i);
    setChosen(next);
  };
  const done = (): void => {
    if (chosen.size === 0) return;
    props.onDone([...chosen].sort((a, b) => a - b));
  };
  return (
    <Dialog
      open
      size="md"
      title={text.t(m.batchTitle)}
      description={text.t(m.batchBody, { count: charts.length })}
      onClose={() => {
        props.onDone(null);
      }}
      footer={
        <div className={s.footer}>
          <Button
            variant="ghost"
            onClick={() => {
              props.onDone(null);
            }}
          >
            {text.t(m.cancel)}
          </Button>
          <Button
            variant="primary"
            icon="FolderInput"
            disabledReason={chosen.size === 0 ? { key: m.batchNone } : null}
            onClick={done}
          >
            {text.t(m.batchImport, { count: chosen.size })}
          </Button>
        </div>
      }
    >
      <div className={s.dialog}>
        {charts.length > 1 && (
          <Checkbox
            label={text.t(m.batchSelectAll)}
            checked={all ? true : chosen.size === 0 ? false : 'mixed'}
            onChange={(v) => {
              setChosen(new Set(v ? charts.map((_, i) => i) : []));
            }}
          />
        )}
        <ul className={s.batchList} aria-label={text.t(m.batchList)}>
          {charts.map((c, i) => (
            <li key={`${c.name}:${String(i)}`} className={s.batchRow}>
              <Checkbox
                label={c.name}
                checked={chosen.has(i)}
                onChange={(v) => {
                  toggle(i, v);
                }}
              />
              <span className={s.muted}>{formatSize(c.bytes, text)}</span>
            </li>
          ))}
        </ul>
      </div>
    </Dialog>
  );
}
